import { useCallback, useState } from 'react';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import logger from 'src/utils/logger';
import { getPreviewClientFilterOptions } from '../preview/endpoints';
import { fetchWazuhClientCompanies } from './wazuhClientCompanies';
import type { SecuritySearchableFilterOption } from './SecuritySearchableFilter';

const toPreviewClientOptions = (): SecuritySearchableFilterOption[] =>
  getPreviewClientFilterOptions().map((option) => ({
    value: option.id,
    label: option.label,
  }));

export const useWazuhClientFilterOptions = (enabled = true) => {
  const { resellerId } = useWazuhScope();
  const { isPreviewMode: endpointsPreview } = useEdrDataGate('endpoints');
  const { isPreviewMode: clientsPreview } = useEdrDataGate('clients');
  const isPreviewMode = endpointsPreview || clientsPreview;
  const [options, setOptions] = useState<SecuritySearchableFilterOption[]>([]);
  const [loading, setLoading] = useState(false);

  const onSearch = useCallback(
    async (query: string) => {
      if (isPreviewMode) {
        const q = query.trim().toLowerCase();
        const all = toPreviewClientOptions();
        setOptions(
          q ? all.filter((option) => option.label.toLowerCase().includes(q)) : all
        );
        return;
      }

      if (!enabled || !resellerId) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const companies = await fetchWazuhClientCompanies(resellerId, query.trim(), 0, 25);
        setOptions(
          companies.map((company) => ({
            value: company.id,
            label: company.name,
          }))
        );
      } catch (error) {
        logger(error, 'error', 'useWazuhClientFilterOptions');
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [enabled, resellerId, isPreviewMode]
  );

  return { options, loading, onSearch };
};
